import { useState, useEffect } from "react";
import { Table, Button, Form, Modal, Pagination } from "react-bootstrap";

const ModalForm = () => {
    const [modalData, setModalData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // States for the edit modal
    const [showEditModal, setShowEditModal] = useState(false);
    const [editingModal, setEditingModal] = useState(null);
    const [formValues, setFormValues] = useState({
        model_name: "",
        maker_id: "",
        year: "" // Added the missing year field
    });

    // States for delete confirmation
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deletingModal, setDeletingModal] = useState(null);

    // Pagination states
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 5;

    useEffect(() => {
        fetchModals();
    }, []);

    const fetchModals = async () => {
        try {
            setLoading(true);
            const response = await fetch("https://motor-easy.clickbooster.io/api/modals");
            if (response.ok) {
                const data = await response.json();
                const modalsArray = Array.isArray(data) ? data : [data];
                setModalData(modalsArray);
                // console.log("Modal data fetched successfully:", modalsArray);
            } else {
                setError("Error fetching modal data");
                console.log("Error fetching modal data");
            }
        } catch (error) {
            setError(`Error: ${error.message}`);
            console.error("Error:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleEditClick = (modal) => {
        setEditingModal(modal);
        setFormValues({
            model_name: modal.model_name,
            maker_id: modal.maker_id,
            year: modal.year || "" // Set the year value from the modal, defaulting to empty string if not present
        });
        setShowEditModal(true);
    };

    const handleDeleteClick = (modal) => {
        setDeletingModal(modal);
        setShowDeleteModal(true);
    };

    const handleCloseModal = () => {
        setShowEditModal(false);
        setEditingModal(null);
    };

    const handleCloseDeleteModal = () => {
        setShowDeleteModal(false);
        setDeletingModal(null);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormValues({
            ...formValues,
            [name]: value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const response = await fetch(`https://motor-easy.clickbooster.io/api/modals/${editingModal.id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(formValues)
            });

            if (response.ok) {
                const updatedModal = await response.json();
                setModalData(modalData.map(modal =>
                    modal.id === editingModal.id ? updatedModal : modal
                ));
                setShowEditModal(false);
                alert("Modal updated successfully!");
            } else {
                const errorData = await response.json();
                alert(`Failed to update: ${errorData.message || "Unknown error"}`);
            }
        } catch (error) {
            console.error("Error updating modal:", error);
            alert(`Error updating modal: ${error.message}`);
        }
    };

    const handleDelete = async () => {
        if (!deletingModal) return;

        try {
            const response = await fetch(`https://motor-easy.clickbooster.io/api/modals/${deletingModal.id}`, {
                method: 'DELETE',
                headers: {
                    'Content-Type': 'application/json',
                }
            });

            if (response.ok) {
                // Remove the deleted modal from state
                setModalData(modalData.filter(modal => modal.id !== deletingModal.id));
                setShowDeleteModal(false);
                alert("Modal deleted successfully!");
            } else {
                const errorData = await response.json();
                alert(`Failed to delete: ${errorData.message || "Unknown error"}`);
            }
        } catch (error) {
            console.error("Error deleting modal:", error);
            alert(`Error deleting modal: ${error.message}`);
        }
    };

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = modalData.slice(indexOfFirstItem, indexOfLastItem);

    const totalPages = Math.ceil(modalData.length / itemsPerPage);

    if (loading) return <div className="text-center my-5"><p>Loading...</p></div>;
    if (error) return <div className="text-center my-5 text-danger"><p>{error}</p></div>;

    return (
        <div className="container mt-4">
            <h2 className="mb-4">Modal Details</h2>

            {modalData.length > 0 ? (
                <>
                    <Table striped bordered hover responsive>
                        <thead>
                            <tr>
                                <th>ID</th>
                                <th>Model Name</th>
                                <th>Maker ID</th>
                                <th>Year</th> {/* Added Year column to the table */}
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentItems.map((modal) => (
                                <tr key={modal.id}>
                                    <td>{modal.id}</td>
                                    <td>{modal.model_name}</td>
                                    <td>{modal.maker_id}</td>
                                    <td>{modal.year}</td> {/* Display year in the table */}
                                    <td>
                                        <div className="d-flex gap-2">
                                            <Button
                                                variant="primary"
                                                size="sm"
                                                onClick={() => handleEditClick(modal)}
                                            >
                                                Edit
                                            </Button>
                                            <Button
                                                variant="danger"
                                                size="sm"
                                                onClick={() => handleDeleteClick(modal)}
                                            >
                                                Delete
                                            </Button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                    <Pagination className="justify-content-center">
                        {[...Array(totalPages).keys()].map(page => (
                            <Pagination.Item
                                key={page + 1}
                                active={page + 1 === currentPage}
                                onClick={() => handlePageChange(page + 1)}
                            >
                                {page + 1}
                            </Pagination.Item>
                        ))}
                    </Pagination>
                </>
            ) : (
                <p>No modal data available.</p>
            )}

            {/* Edit Modal */}
            <Modal show={showEditModal} onHide={handleCloseModal}>
                <Modal.Header closeButton>
                    <Modal.Title>Edit Modal</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form onSubmit={handleSubmit}>
                        <Form.Group className="mb-3">
                            <Form.Label>ID</Form.Label>
                            <Form.Control type="text" value={editingModal?.id || ""} disabled />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Model Name</Form.Label>
                            <Form.Control
                                type="text"
                                name="model_name"
                                value={formValues.model_name}
                                onChange={handleInputChange}
                                required
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Maker ID</Form.Label>
                            <Form.Control
                                type="text"
                                name="maker_id"
                                value={formValues.maker_id}
                                onChange={handleInputChange}
                                required
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Year</Form.Label> {/* Added Year field to the form */}
                            <Form.Control
                                type="text"
                                name="year"
                                value={formValues.year}
                                onChange={handleInputChange}
                                required
                            />
                        </Form.Group>
                        <div className="d-flex justify-content-end gap-2">
                            <Button variant="secondary" onClick={handleCloseModal}>
                                Cancel
                            </Button>
                            <Button variant="primary" type="submit">
                                Save Changes
                            </Button>
                        </div>
                    </Form>
                </Modal.Body>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal show={showDeleteModal} onHide={handleCloseDeleteModal}>
                <Modal.Header closeButton>
                    <Modal.Title>Confirm Delete</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>Are you sure you want to delete the model "{deletingModal?.model_name}"?</p>
                    <p className="text-danger">This action cannot be undone.</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseDeleteModal}>
                        Cancel
                    </Button>
                    <Button variant="danger" onClick={handleDelete}>
                        Delete
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default ModalForm;